import { all, call, put, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import { toastr } from 'react-redux-toastr';

import {
  GET_SKUS,
  CREATE_ORDER,
  PURCHASE_ORDER
} from 'actions/orders/constants';
import {
  getSkusSuccess,
  getSkusError,
  createOrderSuccess,
  createOrderError,
  purchaseOrder,
  purchaseOrderSuccess,
  purchaseOrderError
} from 'actions/orders';
import { hideModal } from 'actions/modal';
import normaliseError from 'utils/normaliseError';

import {
  FORM_STATUSES,
  HTTP_VERBS,
  STRIPE_PAYMENT_INTENT_STATUSES
} from 'utils/constants';

/* Get project skus */
export function* getSkusSaga({ projectId }) {
  const requestUrl = `projects/${projectId}/product_skus`;
  const opts = {
    method: HTTP_VERBS.GET,
    url: requestUrl
  };

  try {
    const response = yield call(axios, opts);
    yield put(getSkusSuccess(projectId, response.data.data));
  } catch (error) {
    const transformedError = normaliseError(error);
    yield put(getSkusError(projectId, transformedError));
  }
}

export function* getSkusWatcher() {
  yield takeLatest(GET_SKUS, getSkusSaga);
}

/* Create order */
export function* createOrderSaga({
  projectId,
  skus,
  paymentMethodId,
  actions
}) {
  const requestUrl = `projects/${projectId}/payment_intents`;
  const opts = {
    method: HTTP_VERBS.POST,
    url: requestUrl,
    data: JSON.stringify({
      skus,
      payment_method_id: paymentMethodId
    })
  };

  try {
    const response = yield call(axios, opts);
    yield put(createOrderSuccess(projectId, response.data.data));

    if (actions) {
      yield call(actions.setFieldValue, 'orderData', response.data.data);
      yield call(actions.setFieldTouched, 'paymentMethodId', false);
      yield call(actions.setSubmitting, false);
    }
  } catch (error) {
    const transformedError = normaliseError(error);
    yield put(createOrderError(projectId, transformedError));

    if (actions) {
      yield call(actions.setErrors, {
        description: transformedError.description
      });
      yield call(actions.setSubmitting, false);
    }
  }
}

export function* createOrderWatcher() {
  yield takeLatest(CREATE_ORDER, createOrderSaga);
}

/* Purchase order */
export function* purchaseOrderSaga({
  projectId,
  paymentIntentId,
  stripeMethods,
  actions
}) {
  const requestUrl = `projects/${projectId}/confirm_payment_intent`;

  const opts = {
    method: HTTP_VERBS.POST,
    url: requestUrl,
    data: JSON.stringify({ payment_intent_id: paymentIntentId })
  };

  try {
    const response = yield call(axios, opts);
    const responseData = response.data.data;
    const { client_secret, credits, status } = responseData;

    if (status === STRIPE_PAYMENT_INTENT_STATUSES.REQUIRES_ACTION) {
      const { error } = yield call(
        stripeMethods.handleCardAction,
        client_secret
      );

      if (!error) {
        // Re-submit to our API, which will attempt again to complete the PaymentIntent
        yield put(
          purchaseOrder(projectId, paymentIntentId, stripeMethods, actions)
        );
      } else {
        throw new Error(error.message);
      }
    }

    if (status === STRIPE_PAYMENT_INTENT_STATUSES.SUCCEEDED) {
      yield put(purchaseOrderSuccess(projectId, credits));
      yield put(hideModal());
      yield call(
        toastr.success,
        `Credit${credits.length > 1 ? 's' : ''} purchased`
      );
    }
  } catch (error) {
    const transformedError = normaliseError(error);
    yield put(purchaseOrderError(projectId, paymentIntentId, transformedError));

    if (actions) {
      yield call(actions.setErrors, {
        description: transformedError.description
      });
      yield call(
        actions.setStatus,
        transformedError.unrecoverable
          ? FORM_STATUSES.UNRECOVERABLE
          : FORM_STATUSES.OKAY
      );
      yield call(actions.setSubmitting, false);
    }
  }
}

export function* purchaseOrderWatcher() {
  yield takeLatest(PURCHASE_ORDER, purchaseOrderSaga);
}

export default function* rootSaga() {
  yield all([
    call(getSkusWatcher),
    call(createOrderWatcher),
    call(purchaseOrderWatcher)
  ]);
}
